import React, { useState, useEffect } from 'react';
import { Typography, Box, Card, CardContent, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useContext } from "react";
import { AuthContext } from "../Auth/AuthContext";
import { formatCurrency } from "../utils/currencyFormatter";

const UpcomingPaymentsChart = ({ data, onPeriodChange }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(1);
  const { userCurrency } = useContext(AuthContext);

  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setSelectedPeriod(newPeriod);
    
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    }
  };

  useEffect(() => {
    if (data && data.months && data.months !== selectedPeriod) {
      setSelectedPeriod(data.months);
    }
  }, [data]);

  if (!data || !data.categoryBreakdown || data.categoryBreakdown.length === 0) {
    return (
      <Box>
        {/* Period Selector */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Upcoming Recurring Payments
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={selectedPeriod}
              label="Period"
              onChange={handlePeriodChange}
            >
              <MenuItem value={1}>Next Month</MenuItem>
              <MenuItem value={3}>Next 3 Months</MenuItem>
              <MenuItem value={6}>Next 6 Months</MenuItem>
              <MenuItem value={12}>Next Year</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            No upcoming payments found for the next {selectedPeriod} month{selectedPeriod > 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>
    );
  }

  const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    // Only show percentage labels for slices larger than 8%
    if (percent < 0.08) return null;
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
        style={{ pointerEvents: 'none' }} // This prevents labels from blocking hover
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const value = data.value;
      const totalAmount = payload[0].payload.amount;
      
      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          padding: '12px 16px',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          fontWeight: '500',
          backdropFilter: 'blur(8px)'
        }}>
          <p style={{ margin: '0 0 4px 0', color: '#333', fontWeight: '600' }}>
            {data.payload.category}
          </p>
          <p style={{ margin: '2px 0', color: '#2563eb' }}>
            Amount: {formatCurrency(totalAmount, userCurrency)}
          </p>
          <p style={{ margin: '2px 0', color: '#059669' }}>
            Payments: {data.payload.count}
          </p>
          <p style={{ margin: '2px 0', color: '#7c3aed' }}>
            Avg: {formatCurrency(totalAmount / data.payload.count, userCurrency)}
          </p>
        </div>
      );
    }
    return null;
  };

  const getPeriodText = () => {
    switch(selectedPeriod) {
      case 1: return 'This Month';
      case 3: return 'Next 3 Months';
      case 6: return 'Next 6 Months';
      case 12: return 'Next Year';
      default: return `Next ${selectedPeriod} Months`;
    }
  };

  return (
    <Box>
      {/* Header with Period Selector */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Upcoming Recurring Payments
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            label="Period"
            onChange={handlePeriodChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value={1}>Next Month</MenuItem>
            <MenuItem value={3}>Next 3 Months</MenuItem>
            <MenuItem value={6}>Next 6 Months</MenuItem>
            <MenuItem value={12}>Next Year</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* Total Due Card */}
      <Card sx={{ 
        mb: 2, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ textAlign: 'center', color: 'white', position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" component="div" fontWeight="bold">
            {formatCurrency(data.totalDue?.toFixed(2) || 0.00, userCurrency)}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Total Due - {getPeriodText()}
          </Typography>
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }}
          />
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(100px, 1fr))" gap={1} mb={2}>
        <Card sx={{ backgroundColor: '#f8f9fa', textAlign: 'center', p: 1 }}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            {data.categoryBreakdown?.length || 0}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Categories
          </Typography>
        </Card>
        
        <Card sx={{ backgroundColor: '#f8f9fa', textAlign: 'center', p: 1 }}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            {data.categoryBreakdown?.reduce((sum, cat) => sum + cat.count, 0) || 0}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Payments
          </Typography>
        </Card>
        
        <Card sx={{ backgroundColor: '#f8f9fa', textAlign: 'center', p: 1 }}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            {data.categoryBreakdown?.length > 0 ? (formatCurrency(data.totalDue / data.categoryBreakdown.reduce((sum, cat) => sum + cat.count, 0).toFixed(0), userCurrency)) : '0'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Avg Payment
          </Typography>
        </Card>
      </Box>

      {/* Pie Chart */}
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={data.categoryBreakdown}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={90}
            fill="#8884d8"
            dataKey="amount"
            nameKey="category"
          >
            {data.categoryBreakdown.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="circle"
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Category Details */}
      <Box mt={2}>
        <Typography variant="subtitle2" gutterBottom fontWeight="medium">
          Payment Breakdown
        </Typography>
        {data.categoryBreakdown.map((category, index) => (
          <Box
            key={category.category}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={1.5}
            mb={1}
            sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
              borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
              '&:hover': {
                backgroundColor: '#e8f4f8',
                transform: 'translateX(4px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {category.category}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {category.count} payment{category.count !== 1 ? 's' : ''} • 
                {formatCurrency((category.amount / category.count).toFixed(2), userCurrency)} avg
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body1" fontWeight="bold" color="primary">
                {formatCurrency(category.amount.toFixed(2), userCurrency)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {((category.amount / data.totalDue) * 100).toFixed(1)}%
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Period Info */}
      <Box mt={2} p={2} sx={{ backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
        <Typography variant="body2" color="primary">
          📅 <strong>Showing payments due in {getPeriodText().toLowerCase()}</strong>
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
          This includes all recurring payments scheduled for the selected period. 
          Change the period above to see different timeframes.
        </Typography>
      </Box>
    </Box>
  );
};

export default UpcomingPaymentsChart;